import { FormEvent, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import PublicLayout from "../layout/PublicLayout";
import DirectoryFilters from "../components/DirectoryFilters";
import TherapistCard from "../components/TherapistCard";
import CrisisNotice from "../components/CrisisNotice";
import { useDirectorySearch } from "../../application/usePublicDirectory";
import { isCrisisQuery } from "../../domain/crisisTerms";
import { DIRECTORY_ENABLED } from "../../domain/availability";
import {
    DirectoryFilters as Filters,
    Modality,
    PresentingProblem,
    TherapyType,
} from "../../domain/types";

/** URL params are the source of truth, so a search is shareable and survives a reload. */
function fromSearchParams(params: URLSearchParams): Filters {
    return {
        q: params.get("q") || undefined,
        problem: (params.get("problem") as PresentingProblem) || undefined,
        therapyType: (params.get("therapy_type") as TherapyType) || undefined,
        modality: (params.get("modality") as Modality) || undefined,
        city: params.get("city") || undefined,
    };
}

function toSearchParams(filters: Filters): URLSearchParams {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.problem) params.set("problem", filters.problem);
    if (filters.therapyType) params.set("therapy_type", filters.therapyType);
    if (filters.modality) params.set("modality", filters.modality);
    if (filters.city) params.set("city", filters.city);
    return params;
}

export default function DirectoryPage() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = fromSearchParams(searchParams);
    const [draftQuery, setDraftQuery] = useState(filters.q ?? "");

    const { data, isLoading, isError, refetch } = useDirectorySearch(
        DIRECTORY_ENABLED ? filters : null
    );

    const applyFilters = (next: Filters) => setSearchParams(toSearchParams(next));

    const submitQuery = (event: FormEvent) => {
        event.preventDefault();
        applyFilters({ ...filters, q: draftQuery.trim() || undefined });
    };

    // Evaluated on what is typed, not on what was searched, so the notice is
    // there before the query is ever sent.
    const showCrisisNotice = isCrisisQuery(draftQuery || filters.q || "", i18n.language);

    if (!DIRECTORY_ENABLED) {
        return (
            <PublicLayout>
                <div className="mx-auto flex max-w-prose flex-col items-start gap-4 rounded-2xl border border-gray-200 p-8 dark:border-gray-800">
                    <span className="rounded-full bg-accent-tint px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.08em] text-accent-on-tint">
                        {t("publicDirectory.settings.beta")}
                    </span>
                    <h1 className="text-title-sm font-bold tracking-tight text-content-primary">
                        {t("publicDirectory.closed.title")}
                    </h1>
                    <p className="text-content-secondary">
                        {t("publicDirectory.closed.body")}
                    </p>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-title-sm font-bold tracking-tight text-content-primary">
                        {t("publicDirectory.title")}
                    </h1>
                    <p className="text-content-secondary">{t("publicDirectory.subtitle")}</p>
                </div>

                <form onSubmit={submitQuery} className="flex flex-wrap gap-2">
                    <input
                        type="search"
                        value={draftQuery}
                        onChange={(e) => setDraftQuery(e.target.value)}
                        placeholder={t("publicDirectory.searchPlaceholder")}
                        aria-label={t("publicDirectory.searchLabel")}
                        maxLength={120}
                        className="min-w-0 flex-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-surface-raised px-4 py-3 text-content-primary placeholder:text-content-tertiary focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    />
                    <button
                        type="submit"
                        className="rounded-xl bg-brand-500 px-6 py-3 font-semibold text-white hover:bg-brand-600 focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
                    >
                        {t("publicDirectory.search")}
                    </button>
                </form>

                {showCrisisNotice && <CrisisNotice />}

                <DirectoryFilters filters={filters} onChange={applyFilters} />

                {isLoading && (
                    <p className="text-theme-sm text-content-tertiary">{t("common.loading")}</p>
                )}

                {isError && (
                    <div className="flex flex-col items-start gap-3 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                        <p className="text-content-secondary">{t("publicDirectory.error")}</p>
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-2 text-theme-sm text-content-primary"
                        >
                            {t("publicDirectory.retry")}
                        </button>
                    </div>
                )}

                {data && data.entries.length === 0 && (
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-8 text-center">
                        <p className="font-medium text-content-primary">
                            {t("publicDirectory.empty.title")}
                        </p>
                        <p className="mt-1 text-theme-sm text-content-secondary">
                            {t("publicDirectory.empty.body")}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setDraftQuery("");
                                applyFilters({});
                            }}
                            className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-2 text-theme-sm text-content-primary"
                        >
                            {t("publicDirectory.empty.clear")}
                        </button>
                    </div>
                )}

                {data && data.entries.length > 0 && (
                    <>
                        <p className="text-theme-sm text-content-tertiary">
                            {t("publicDirectory.resultCount", { count: data.entries.length })}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {data.entries.map((entry) => (
                                <TherapistCard key={entry.slug} entry={entry} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </PublicLayout>
    );
}
